import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  ArrowDown,
  Loader2,
  Upload,
  Sparkles,
  ShieldCheck,
  Zap,
  Send,
  Lightbulb,
} from "lucide-react";
import { toast } from "sonner";
import QuickActionGrid from "../components/grapho/QuickActionGrid";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLANS = {
  trial: { name: "Launch Trial", credits: 1 },
  starter: { name: "Starter Scale", credits: 150 },
  growth: { name: "Growth Pro", credits: 250 },
  empire: { name: "Empire Velocity", credits: 400 },
};

const NICHE_OPTIONS = [
  "Teacher",
  "Mom / Family",
  "Pets",
  "Sports",
  "Western",
  "Fishing",
  "Hunting",
  "Fitness",
  "Faith",
  "Patriotic",
  "Wedding",
  "Baby",
  "Grandma / Family Titles",
  "Profession",
  "Seasonal",
  "Funny / Sarcastic",
  "Retro",
  "Coquette",
  "Minimalist",
  "Custom / Other",
];

const TYPOGRAPHY_STYLES = [
  "Groovy/Retro",
  "Vintage Sports",
  "Preppy Coquette",
  "Western Distressed",
  "Bold Clean Minimalist",
];

const WORKFLOW_STEPS = [
  { num: "01", title: "You Brief", desc: "Tell us the idea, phrase, niche or reference." },
  { num: "02", title: "Grapho Thinks", desc: "Creative direction, trend interpretation, style development and original execution." },
  { num: "03", title: "Grapho Produces", desc: "Your design moves through production and quality control." },
  { num: "04", title: "You Wake Up", desc: "Your finished files are waiting in your workspace." },
];

const EXAMPLE_PROMPTS = [
  "Funny teacher appreciation shirt about surviving Monday mornings.",
  "I found this style trending in my niche. Create a completely original version for Halloween.",
  "I want a cute Christmas design for dog moms. Surprise me.",
];

const SURPRISE_MESSAGE =
  "Perfect. Give us the basic idea and we'll handle the creative direction, visual style, supporting elements and composition.";

function SparklesSubmit() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l1.9 5.7a2 2 0 001.3 1.3L21 11l-5.8 1.9a2 2 0 00-1.3 1.3L12 20l-1.9-5.8a2 2 0 00-1.3-1.3L3 11l5.8-1.9a2 2 0 001.3-1.3L12 3z" />
    </svg>
  );
}

export default function WelcomePage() {
  const params = useSearchParams()[0];
  const navigate = useNavigate();
  const planKey = params.get("plan") || "trial";
  const plan = PLANS[planKey] || PLANS.trial;

  const [form, setForm] = useState({ email: params.get("email") || "", request: "", style: "", reference_links: "", niche: "" });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [surpriseNote, setSurpriseNote] = useState(false);
  const [ideaOpen, setIdeaOpen] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Permanent dashboard: after the first submission, this onboarding never shows again.
  useEffect(() => {
    const em = params.get("email");
    if (!em) return;
    axios
      .post(`${API}/dashboard`, { email: em })
      .then(({ data: d }) => {
        if (d?.customer && d?.briefs?.length > 0) {
          navigate(`/dashboard?email=${encodeURIComponent(em)}`, { replace: true });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTypography = (label) =>
    setForm((f) => {
      const parts = f.style.split(",").map((s) => s.trim()).filter(Boolean);
      const idx = parts.indexOf(label);
      if (idx >= 0) parts.splice(idx, 1);
      else parts.push(label);
      return { ...f, style: parts.join(", ") };
    });

  const activeTypography = form.style.split(",").map((s) => s.trim());

  const surprise = () => {
    setSurpriseNote(true);
    setForm((f) => ({ ...f, request: SURPRISE_MESSAGE }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError("Please enter the email you used at checkout.");
      return;
    }
    if (form.request.trim().length < 5) {
      setError("Tell us what to design — even a rough idea works.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("email", form.email.trim());
      fd.append("request", form.request.trim());
      fd.append("style", form.style.trim());
      fd.append("niche", form.niche);
      fd.append("reference_links", form.reference_links.trim());
      fd.append("plan", planKey);
      if (file) fd.append("ref_image", file);
      const { data } = await axios.post(`${API}/briefs/submit`, fd);
      setResult(data);
      setSurpriseNote(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Submission failed — please try again.");
    }
    setSubmitting(false);
  };

  const creditsAfter = Math.max(0, plan.credits - 1);

  return (
    <div data-testid="welcome-page" className="min-h-screen px-4 pb-24 pt-24 sm:px-6">
      <div
        data-testid="welcome-overnight-promise"
        className="sticky top-3 z-30 mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-2xl border border-amber-400/50 bg-[#0A1020]/95 px-5 py-4 shadow-[0_10px_40px_rgba(245,158,11,0.15)] backdrop-blur"
      >
        <Zap className="h-4 w-4 shrink-0 text-amber-300" />
        <p className="text-sm font-bold tracking-wide text-amber-300 sm:text-base">
          You brief it tonight. We build it while you sleep.
        </p>
        <span
          data-testid="welcome-banner-credits"
          className="w-fit rounded-full border border-amber-400/50 bg-amber-400/10 px-3.5 py-1.5 text-xs font-bold text-amber-300 sm:ml-auto sm:w-auto"
        >
          {plan.credits} credit{plan.credits === 1 ? "" : "s"} available
        </span>
      </div>

      <div className="mx-auto mt-8 max-w-6xl">
        <Link
          to="/"
          data-testid="welcome-back-link"
          className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Grapho
        </Link>

        <div className="mt-6 grid items-start gap-8 lg:grid-cols-[1fr_300px]">
          <div className="glass rounded-3xl p-7 sm:p-10">
            {params.get("activated") === "1" && (
              <div
                data-testid="welcome-activated-banner"
                className="mb-8 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-6 text-center"
              >
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.28em] text-amber-300">
                  Welcome to Grapho.
                </p>
                <p className="mt-2 text-xl font-extrabold tracking-tight text-white">Your studio is ready.</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  Your design credits are active and your first creative brief is waiting.
                </p>
                <button
                  data-testid="welcome-enter-studio-button"
                  onClick={() => document.getElementById("welcome-form-anchor")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-sm font-bold text-[#1A1204] transition-all duration-300 hover:bg-amber-300"
                >
                  <Sparkles className="h-4 w-4" />
                  Enter Your Studio
                </button>
                <p className="mt-3 text-xs text-slate-500">No setup required. Start with your first idea.</p>
              </div>
            )}

            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-400">
              First brief // post-payment
            </p>
            <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Welcome to Your Private Creative Studio
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
              Your first brief is ready. You don't need to know exactly how to design it. Tell Grapho
              what you're trying to create — even if the idea is rough — and our creative workflow will
              turn it into an original production-ready concept.
            </p>
            <p
              data-testid="welcome-plan-badge"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3.5 py-1.5 text-xs font-semibold text-amber-300"
            >
              {plan.name} plan · {plan.credits} credit{plan.credits > 1 ? "s" : ""} on your account
            </p>

            {result ? (
              <div
                data-testid="welcome-confirmation"
                className="mt-8 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-6 text-center"
              >
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.28em] text-emerald-300">
                  Brief received
                </p>
                <p className="mt-3 text-lg font-bold text-emerald-100">
                  Your first creative request is officially in the studio.
                </p>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Creative direction → Production → Quality review → Delivery
                </p>
                <p className="mt-5 text-sm font-semibold text-white">You're done for now.</p>
                <p className="mt-1 text-sm text-slate-400">We'll take it from here.</p>
                <Link
                  data-testid="welcome-dashboard-link"
                  to={`/dashboard?email=${encodeURIComponent(form.email.trim())}`}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-sm font-bold text-[#1A1204] transition-all duration-300 hover:bg-amber-300"
                >
                  Open My Studio
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="mt-8 space-y-5" id="welcome-form-anchor">
                <div>
                  <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      What's the Idea?
                    </label>
                    <button
                      type="button"
                      data-testid="welcome-surprise-button"
                      onClick={surprise}
                      className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-[11px] font-bold text-amber-300 transition-colors hover:bg-amber-400/20"
                    >
                      <Lightbulb className="h-3 w-3" />
                      Surprise Me
                    </button>
                  </div>
                  <textarea
                    data-testid="welcome-input-request"
                    rows={4}
                    value={form.request}
                    onChange={set("request")}
                    placeholder="Tell us what you're thinking — even if it's rough."
                    className="w-full resize-none rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-3.5 text-sm leading-relaxed text-slate-100 placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-400/20"
                  />
                  {surpriseNote && (
                    <p
                      data-testid="welcome-surprise-note"
                      className="mt-2 rounded-xl border border-amber-400/30 bg-amber-400/5 px-4 py-2.5 text-xs leading-relaxed text-amber-200"
                    >
                      {SURPRISE_MESSAGE}
                    </p>
                  )}
                  <div className="mt-2.5 space-y-1">
                    {EXAMPLE_PROMPTS.map((ex, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`welcome-example-prompt-${i}`}
                        onClick={() => setForm((f) => ({ ...f, request: ex }))}
                        className="block w-full text-left text-xs text-slate-500 transition-colors hover:text-amber-300"
                      >
                        Try: "{ex}"
                      </button>
                    ))}
                  </div>
                </div>

                <QuickActionGrid
                  prefix="welcome"
                  onPrefill={(text) => setForm((f) => ({ ...f, request: text }))}
                />

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Email Address
                  </label>
                  <input
                    data-testid="welcome-input-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="seller@yourshop.com"
                    className="w-full rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-sky-400/60 focus:outline-none focus:ring-2 focus:ring-sky-400/20"
                  />
                </div>

                <div>
                  <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Target Niche
                    </label>
                    <button
                      type="button"
                      data-testid="welcome-no-idea-button"
                      onClick={() => setIdeaOpen((o) => !o)}
                      className="text-[11px] font-semibold text-sky-400 transition-colors hover:text-sky-300"
                    >
                      Not sure what to make?
                    </button>
                  </div>
                  <select
                    data-testid="welcome-input-niche"
                    value={form.niche}
                    onChange={set("niche")}
                    className="w-full rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-3.5 text-sm text-slate-100 focus:border-sky-400/60 focus:outline-none focus:ring-2 focus:ring-sky-400/20"
                  >
                    <option value="">No specific niche — artist's choice</option>
                    {NICHE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  {ideaOpen && (
                    <div
                      data-testid="welcome-no-idea-panel"
                      className="mt-2.5 rounded-xl border border-sky-400/25 bg-sky-400/5 px-4 py-3"
                    >
                      <p className="text-xs leading-relaxed text-slate-300">
                        Not sure what to make? Tell Grapho the audience you're selling to and we'll help
                        develop the creative direction.
                      </p>
                      <button
                        type="button"
                        data-testid="welcome-no-idea-example"
                        onClick={() => setForm((f) => ({ ...f, request: "I sell to teachers. Give me 5 design ideas with strong Etsy potential for the upcoming season." }))}
                        className="mt-2 block text-left text-xs italic text-sky-300 transition-colors hover:text-sky-200"
                      >
                        Try: "I sell to teachers. Give me 5 design ideas with strong Etsy potential for
                        the upcoming season."
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Style Preferences
                  </label>
                  <input
                    data-testid="welcome-input-style"
                    type="text"
                    value={form.style}
                    onChange={set("style")}
                    placeholder="e.g. retro 70s, minimal line art, bold mascot"
                    className="w-full rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-sky-400/60 focus:outline-none focus:ring-2 focus:ring-sky-400/20"
                  />
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {TYPOGRAPHY_STYLES.map((t) => {
                      const active = activeTypography.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          data-testid={`welcome-typography-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => toggleTypography(t)}
                          className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-all duration-200 ${
                            active
                              ? "border-amber-400 bg-amber-400 text-[#1A1204]"
                              : "border-amber-400/40 bg-amber-400/5 text-amber-300/90 hover:border-amber-300"
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Drop Your References Here
                  </label>
                  <label
                    htmlFor="ref-upload"
                    data-testid="welcome-input-ref"
                    className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-white/15 bg-[#070B14]/60 px-4 py-4 text-sm text-slate-400 transition-colors hover:border-amber-400/40 hover:text-slate-200"
                  >
                    <Upload className="h-4 w-4 shrink-0 text-amber-300" />
                    {file ? file.name : "Upload up to 10 images — inspiration, competitors, your previous designs, screenshots or examples of the direction you like"}
                  </label>
                  <input
                    id="ref-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                  <p className="mt-2 flex items-start gap-1.5 text-xs leading-snug text-slate-500">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                    Grapho uses references to understand creative direction — not to reproduce someone
                    else's artwork.
                  </p>
                  <p className="mt-1.5 text-xs leading-snug text-slate-500">
                    Credits are charged based on the number of designs requested, not the number of
                    reference images uploaded.
                  </p>
                </div>

                {error && (
                  <p data-testid="welcome-error" className="text-sm text-rose-400">
                    {error}
                  </p>
                )}

                <div
                  data-testid="welcome-credit-preview"
                  className="rounded-xl border border-sky-400/25 bg-sky-400/5 px-4 py-3 text-xs font-semibold text-sky-200"
                >
                  1 design requested — 1 credit — {creditsAfter} credit{creditsAfter === 1 ? "" : "s"}{" "}
                  remaining after submission.
                </div>

                <button
                  data-testid="welcome-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-amber-400 px-8 py-4 text-base font-bold text-[#1A1204] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {submitting ? "Sending to the studio..." : "Send to the Studio — 1 Credit"}
                </button>
                <p className="text-center text-xs text-slate-600">
                  Your brief enters the production queue immediately.
                </p>
              </form>
            )}
          </div>

          <aside data-testid="welcome-workflow-sidebar" className="glass h-fit rounded-3xl p-6 lg:sticky lg:top-24">
            <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-sky-400">
              Your Overnight Workflow
            </h3>
            <div className="mt-5 space-y-1">
              {WORKFLOW_STEPS.map((step, i) => (
                <div key={step.num}>
                  <div className="flex gap-3">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-400/40 bg-amber-400/10 text-xs font-bold text-amber-300">
                      {step.num}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white">{step.title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{step.desc}</p>
                    </div>
                  </div>
                  {i < WORKFLOW_STEPS.length - 1 && (
                    <ArrowDown className="my-1.5 ml-3.5 h-4 w-4 text-amber-400/60" />
                  )}
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}